/* =====================================================================
 * Chip — 공통 칩/태그 컴포넌트 (테마 기반)
 * ===================================================================== */

import React from 'react';
import styled, { css } from 'styled-components';

type ChipVariant = 'outlined' | 'filled' | 'subtle';
type ChipSize = 'sm' | 'md';

interface ChipProps extends React.HTMLAttributes<HTMLSpanElement> {
  variant?: ChipVariant;
  size?: ChipSize;
  icon?: React.ReactNode;
  /** 클릭 가능 여부 (버튼처럼 동작) */
  clickable?: boolean;
  active?: boolean;
  children?: React.ReactNode;
}

export const Chip: React.FC<ChipProps> = ({
  variant = 'outlined',
  size = 'sm',
  icon,
  clickable = false,
  active = false,
  children,
  ...props
}) => (
  <StyledChip
    $variant={variant}
    $size={size}
    $clickable={clickable}
    $active={active}
    as={clickable ? 'button' : 'span'}
    {...props}
  >
    {icon && <ChipIcon>{icon}</ChipIcon>}
    {children}
  </StyledChip>
);

const variantStyles = {
  outlined: css`
    background: transparent;
    border: 1px solid ${({ theme }) => theme.colors.border};
    color: ${({ theme }) => theme.colors.textSecondary};
  `,
  filled: css`
    background: ${({ theme }) => theme.colors.bgSecondary};
    border: 1px solid ${({ theme }) => theme.colors.border};
    color: ${({ theme }) => theme.colors.textSecondary};
  `,
  subtle: css`
    background: ${({ theme }) =>
      theme.mode === 'dark' ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.04)'};
    border: 1px solid transparent;
    color: ${({ theme }) => theme.colors.textSecondary};
  `,
};

const StyledChip = styled.span<{
  $variant: ChipVariant;
  $size: ChipSize;
  $clickable: boolean;
  $active: boolean;
}>`
  display: inline-flex;
  align-items: center;
  gap: 5px;
  font-weight: 500;
  white-space: nowrap;
  border-radius: 999px;
  line-height: 1;
  transition: all ${({ theme }) => theme.transitions.fast};

  ${({ $size }) =>
    $size === 'sm'
      ? css`
          padding: 3px 10px;
          font-size: 0.6875rem;
        `
      : css`
          padding: 4px 12px;
          font-size: 0.75rem;
        `}

  ${({ $variant }) => variantStyles[$variant]}

  ${({ $clickable, theme }) =>
    $clickable &&
    css`
      cursor: pointer;
      &:hover {
        background: ${theme.colors.bgHover};
        border-color: ${theme.colors.borderInput};
      }
    `}

  ${({ $active, theme }) =>
    $active &&
    css`
      background: ${theme.colors.accentLight};
      border-color: ${theme.colors.accent};
      color: ${theme.colors.textPrimary};
    `}
`;

const ChipIcon = styled.span`
  display: flex;
  align-items: center;
  flex-shrink: 0;
`;
